import React from "react";
import { useTranslation } from "react-i18next";
import { Tag as T } from "@planzone/dto";
import { useRetrieveResourcesTags } from "@planzone/view-models";

import { TagIcon } from "@common/assets/tagIcon";
import { Loader } from "../loader";
import { Section } from "../section";
import {
  extractResourcesFromTag,
  extractTags,
  filterSearch,
  useAllPlanzoneResources,
  useProjectMembers,
} from "../utils";
import { Props } from "./types";
import { Tag } from "./tag";
import styles from "./styles.scss";

function ResourceTags({
  search,
  allocatedResources,
  style,
  select,
  unSelect,
  isOpen,
}: Props) {
  const { t } = useTranslation();
  const { isInProject, isRequestPending, members } = useProjectMembers();
  const planzoneResources = useAllPlanzoneResources();
  const planzoneTags = useRetrieveResourcesTags();
  // Outside a project, the tags come from their endpoint: the resources are not loaded until a tag is
  // allocated, then all its resources are.
  const tags = isInProject ? extractTags(members) : planzoneTags.tags;
  const isPending = isInProject
    ? isRequestPending
    : planzoneTags.isRequestPending;
  const selectTag = (tag: T) => {
    const allocate = (resources: typeof members) =>
      select(
        extractResourcesFromTag(resources, tag).filter(
          (resource) =>
            !allocatedResources.find(({ uuid }) => uuid === resource.uuid),
        ),
      );

    isInProject
      ? allocate(members)
      : planzoneResources.withAllResources(allocate);
  };
  const unSelectTag = (tag: T) =>
    unSelect(extractResourcesFromTag(allocatedResources, tag));

  React.useEffect(() => {
    !isInProject &&
      !planzoneTags.isRequestPending &&
      !planzoneTags.isRequestSuccess &&
      !planzoneTags.isRequestFailure &&
      planzoneTags.retrieveResourcesTags();
  }, []);

  return (
    <Section
      header={
        <div className={styles.header}>
          <TagIcon className={styles.icon} />
          <p className={styles.title}>
            {t("common.filterMenu.resourceTags.title")}
          </p>
        </div>
      }
      list={
        <div className={styles.list}>
          {!isPending && !planzoneResources.isRequestPending ? (
            filterSearch(tags, search).map((tag) => (
              <Tag
                key={tag.name}
                tag={tag}
                isSelected={
                  !!allocatedResources.find(
                    ({ tags }) =>
                      tags && tags.find(({ name }) => name === tag.name),
                  )
                }
                select={() => selectTag(tag)}
                unSelect={() => unSelectTag(tag)}
              />
            ))
          ) : (
            <Loader />
          )}
        </div>
      }
      style={style}
      isOpen={isOpen}
    />
  );
}

export { ResourceTags };
